import { Button, ColumnTable } from '@octano/global-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, UncontrolledTooltip } from 'reactstrap';
import { useHistory } from 'react-router';

import { moneyFormatter } from '../../../../../utils/currency';
import { PathsLayouts } from '../../../../../config/routes';
import { StudentListItem } from '../types';

const dictPrefix = 'financialPortal.currentAccount.list.table';
const statusPrefix = 'financialPortal.currentAccount.list.status';

const STATUS_COLORS: Record<StudentListItem['financialStatus'], string> = {
  active: '#CEF0DA',
  financialBlock: '#F2C0BD',
  temporaryUnblock: '#FFE6A1',
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '-';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

interface UseStudentListColumnsOptions {
  onEdit: (row: StudentListItem) => void;
  onDelete: (row: StudentListItem) => void;
}

export default function useStudentListColumns({
  onEdit,
  onDelete,
}: UseStudentListColumnsOptions): ColumnTable<StudentListItem>[] {
  const { t } = useTranslation();
  const history = useHistory();
  const formatter = useMemo(() => moneyFormatter(), []);

  return useMemo(
    () => [
      {
        columnName: 'name',
        headerText: t(`${dictPrefix}.nameAndId`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => (
          <div>
            <div className="fw-600">{row.name}</div>
            <div className="text-muted fs-14">{row.identification}</div>
          </div>
        ),
      },
      {
        columnName: 'studyPlans',
        headerText: t(`${dictPrefix}.studyPlan`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          if (!row.studyPlans || row.studyPlans.length === 0) return '-';
          if (row.studyPlans.length === 1) return row.studyPlans[0].name;
          return (
            <>
              <span id={`plans-${row.studentId}`}>
                {t(`${dictPrefix}.multipleStudyPlans`)}
              </span>
              <UncontrolledTooltip target={`plans-${row.studentId}`}>
                {row.studyPlans.map((sp) => sp.name).join(', ')}
              </UncontrolledTooltip>
            </>
          );
        },
      },
      {
        columnName: 'school',
        headerText: t(`${dictPrefix}.school`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => row.school || '-',
      },
      {
        columnName: 'nextDueDate',
        headerText: t(`${dictPrefix}.nextDueDate`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => formatDate(row.nextDueDate),
      },
      {
        columnName: 'outstandingDebt',
        headerText: t(`${dictPrefix}.outstandingDebt`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => formatter.format(row.outstandingDebt),
      },
      {
        columnName: 'balanceInFavor',
        headerText: t(`${dictPrefix}.balanceInFavor`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => formatter.format(row.balanceInFavor),
      },
      {
        columnName: 'financialStatus',
        width: 10,
        headerText: t(`${dictPrefix}.status`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => (
          <Badge
            pill
            className="fs-16 fw-400 py-2 px-4 text-dark"
            style={{
              backgroundColor: STATUS_COLORS[row.financialStatus],
              whiteSpace: 'nowrap',
              width: '100%',
            }}
          >
            {t(`${statusPrefix}.${row.financialStatus}`)}
          </Badge>
        ),
      },
      {
        width: 180,
        columnName: 'action',
        headerText: t(`${dictPrefix}.action`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => {
          const showEdit = row.financialStatus === 'financialBlock';
          const showTrash = row.financialStatus === 'temporaryUnblock';

          return (
            <div
              className="d-flex justify-content-end align-items-center"
              style={{ gap: '8px' }}
            >
              {showEdit && (
                <Button
                  text=""
                  icon="edit"
                  size="sm"
                  outlined
                  onClick={() => onEdit(row)}
                />
              )}
              {showTrash && (
                <Button
                  text=""
                  icon="trash"
                  size="sm"
                  outlined
                  onClick={() => onDelete(row)}
                />
              )}
              <Button
                text={t(`${dictPrefix}.viewDetail`)}
                size="sm"
                onClick={() =>
                  history.push(
                    `${PathsLayouts.currentAccount}/${row.studentId}`,
                  )
                }
              />
            </div>
          );
        },
      },
    ],
    [t, history, formatter, onEdit, onDelete],
  );
}
